import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { differenceInCalendarDays } from 'date-fns';
import { filter, switchMap, take } from 'rxjs';
import {
  AppRoutes,
  DateFormats,
  DrillScoring,
  DrillsRoutes,
  NAVIGATION_TOKEN,
  NavigationProvider,
  ProgressRoutes,
  SessionRoutes,
  formatDateToString
} from '@core';
import { SessionStore } from '@session';
import {
  SzActionMenuItem,
  SzAppShellComponent,
  SzButtonComponent,
  SzCardComponent,
  SzCtaBarComponent,
  SzIcCircleComponent,
  SzIconComponent,
  SzPageTitleComponent,
  SzConfirmDialogService,
  SzPillComponent,
  SzRowCardComponent,
  SzSectionHeadComponent,
  SzSkeletonComponent,
  SzTopBarComponent,
  SzTypePillComponent
} from '@ui';
import {
  CreateDrillCommandPayload,
  DeleteDrillCommandHandler,
  DrillDetailQueryHandler,
  DrillDetailQueryResult,
  ToggleFavoriteCommandHandler
} from '../../application/handlers';
import { DrillDetailViewModel, DrillSessionPreviewViewModel } from '../../application/models';
import { DrillDraftStorage } from '../../application/state/drill-draft.storage';

interface SessionRowVm {
  readonly sessionId: string;
  readonly primaryLabel: string;
  readonly relativeDateLabel: string;
  readonly runsLabel: string;
}

@Component({
  selector: 'lib-drill-detail',
  templateUrl: './drill-detail.component.html',
  styleUrls: ['./drill-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzPillComponent,
    SzTypePillComponent,
    SzCardComponent,
    SzRowCardComponent,
    SzSectionHeadComponent,
    SzCtaBarComponent,
    SzButtonComponent,
    SzIconComponent,
    SzIcCircleComponent,
    SzSkeletonComponent
  ],
  providers: [DrillDetailQueryHandler, ToggleFavoriteCommandHandler, DeleteDrillCommandHandler]
})
export class DrillDetailComponent implements OnInit {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly sessionStore: SessionStore = inject(SessionStore);
  private readonly detailHandler: DrillDetailQueryHandler = inject(DrillDetailQueryHandler);
  private readonly toggleFavoriteHandler: ToggleFavoriteCommandHandler = inject(ToggleFavoriteCommandHandler);
  private readonly deleteHandler: DeleteDrillCommandHandler = inject(DeleteDrillCommandHandler);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);
  private readonly drillDraftStorage: DrillDraftStorage = inject(DrillDraftStorage);
  private readonly navigation: NavigationProvider = inject(NAVIGATION_TOKEN);

  readonly data: WritableSignal<DrillDetailQueryResult | null> = signal<DrillDetailQueryResult | null>(null);

  readonly recentRows: Signal<readonly SessionRowVm[]> = computed((): readonly SessionRowVm[] => {
    const drill: DrillDetailViewModel | undefined = this.data()?.drill;
    if (drill === undefined) return [];
    const today: Date = new Date();
    return drill.recentSessions.map((s: DrillSessionPreviewViewModel): SessionRowVm => this.buildRow(s, today));
  });

  readonly bestSessionRows: Signal<readonly SessionRowVm[]> = computed((): readonly SessionRowVm[] => {
    const best: ReadonlyArray<DrillSessionPreviewViewModel> | undefined = this.data()?.drill.bestSessions;
    if (best === undefined || best.length === 0) return [];
    const today: Date = new Date();
    return best.map((s: DrillSessionPreviewViewModel): SessionRowVm => this.buildRow(s, today));
  });

  readonly actionItems: Signal<readonly SzActionMenuItem[]> = computed((): readonly SzActionMenuItem[] => {
    const r: DrillDetailQueryResult | null = this.data();
    if (r === null) return [];
    return [
      {
        id: 'favorite',
        icon: 'star',
        label: this.translate.instant(
          r.drill.favorite ? 'drills.detail.unfavoriteAction' : 'drills.detail.favoriteAction'
        )
      },
      {
        id: 'edit',
        icon: 'edit',
        label: this.translate.instant('drills.detail.editAction')
      },
      {
        id: 'duplicate',
        icon: 'copy',
        label: this.translate.instant('drills.detail.duplicateAction')
      },
      {
        id: 'delete',
        icon: 'trash',
        label: this.translate.instant('drills.detail.deleteAction'),
        destructive: true
      }
    ];
  });

  ngOnInit(): void {
    this.refresh();
  }

  startSession(): void {
    const drill: DrillDetailViewModel | undefined = this.data()?.drill;
    if (drill === undefined) return;
    if (this.requiresQuickSetup(drill.scoring)) {
      void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.QUICK_SETUP, drill.id]);
      return;
    }
    this.sessionStore.initDraft([
      {
        drillId: drill.id,
        drillName: drill.name,
        drillDescription: drill.description,
        planNote: null,
        scoring: drill.scoring,
        shotsCount: drill.shotsCount,
        targetRuns: null,
        runs: []
      }
    ]);
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.ACTIVE]);
  }

  toggleFavorite(): void {
    const drillId: string | undefined = this.data()?.drill.id;
    if (drillId === undefined) return;
    this.toggleFavoriteHandler
      .execute(drillId)
      .pipe(take(1))
      .subscribe(() => this.refresh());
  }

  deleteDrill(): void {
    const drillId: string | undefined = this.data()?.drill.id;
    if (drillId === undefined) return;
    this.confirmDialog
      .confirm$({
        header: this.translate.instant('drills.detail.deleteConfirm'),
        okLabel: this.translate.instant('common.delete'),
        destructive: true
      })
      .pipe(
        filter((ok: boolean): boolean => ok),
        switchMap(() => this.deleteHandler.execute(drillId)),
        take(1)
      )
      .subscribe((): void => {
        void this.router.navigate(['/', AppRoutes.DRILLS]);
      });
  }

  goToEdit(): void {
    const drillId: string | undefined = this.data()?.drill.id;
    if (drillId === undefined) return;
    void this.router.navigate(['/', AppRoutes.DRILLS, DrillsRoutes.EDIT, drillId]);
  }

  duplicateDrill(): void {
    const drill: DrillDetailViewModel | undefined = this.data()?.drill;
    if (drill === undefined) return;

    const copyFrom: CreateDrillCommandPayload = {
      name: this.translate.instant('drills.detail.copyName', { name: drill.name }),
      type: drill.type,
      scoring: drill.scoring,
      shotsCount: drill.shotsCount,
      description: drill.description,
      favorite: false
    };
    this.drillDraftStorage.setCopySeed(copyFrom);
    this.navigation.replace(['/', AppRoutes.DRILLS, DrillsRoutes.CREATE]);
  }

  goToProgress(): void {
    const drillId: string | undefined = this.data()?.drill.id;
    if (drillId === undefined) return;
    void this.router.navigate(['/', AppRoutes.PROGRESS, ProgressRoutes.DRILL, drillId]);
  }

  goToSession(sessionId: string): void {
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.SUMMARY, sessionId]);
  }

  onActionSelect(id: string): void {
    switch (id) {
      case 'favorite':
        this.toggleFavorite();
        return;
      case 'edit':
        this.goToEdit();
        return;
      case 'duplicate':
        this.duplicateDrill();
        return;
      case 'delete':
        this.deleteDrill();
        return;
    }
  }

  private buildRow(preview: DrillSessionPreviewViewModel, today: Date): SessionRowVm {
    return {
      sessionId: preview.sessionId,
      primaryLabel: this.sessionPrimaryLabel(preview),
      relativeDateLabel: this.relativeDateLabel(preview.date, today),
      runsLabel: `${preview.runsCount} runs`
    };
  }

  private sessionPrimaryLabel(preview: DrillSessionPreviewViewModel): string {
    switch (preview.scoring) {
      case DrillScoring.TimePerRep:
        return preview.bestTime === null ? '-' : `${preview.bestTime.toFixed(2)}s`;
      case DrillScoring.RepsOnly:
        return `${preview.totalReps} reps`;
      case DrillScoring.HitFactor:
        return preview.bestHitFactor === null ? '-' : `HF ${preview.bestHitFactor.toFixed(2)}`;
    }
  }

  private relativeDateLabel(date: Date, today: Date): string {
    const diff: number = differenceInCalendarDays(today, date);
    if (diff <= 0) return this.translate.instant('drills.detail.today');
    if (diff === 1) return this.translate.instant('drills.detail.daysAgoOne');
    if (diff < 7) return this.translate.instant('drills.detail.daysAgo', { days: diff });
    return formatDateToString(date, DateFormats.DayMonthNameLong);
  }

  private requiresQuickSetup(scoring: DrillScoring): boolean {
    return scoring === DrillScoring.TimePerRep || scoring === DrillScoring.HitFactor;
  }

  private refresh(): void {
    const drillId: string = this.route.snapshot.paramMap.get('id') ?? '';
    this.detailHandler
      .query(drillId)
      .pipe(take(1))
      .subscribe((r: DrillDetailQueryResult) => this.data.set(r));
  }
}
